import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SideSheet from '../SideSheet/SideSheet';
import Checkbox from '../Checkbox/Checkbox';
import { SelectorButton } from './SelectorButton.inner.js';
import * as S from './ItemsSelector.styles';
import Button from '../Button/Button';
import SearchInput from '../SearchInput/SearchInput';
import NoItems from './NoItems.inner';

const ItemsSelector = ({
  title,
  initialData,
  onChange,
  renderItem,
  outFormat,
  closeOnApply,
  filterCallback,
  searchLabel,
  searchPlaceholder,
  onDismiss,
  previousStepAction,
  id,
  bigCommerce,
  cuit,
}) => {
  const getInitialData = () =>
    initialData.map((i, localId) => ({ ...i, localId }));

  const [items, setItems] = useState(getInitialData());
  const [allItems, setAllItems] = useState(getInitialData());
  const [isSideSheetOpen, setSideSheetOpen] = useState(false);

  useEffect(() => {
    setAllItems(getInitialData());
    setItems(getInitialData());
  }, []);

  const allItemsSelectedQuantity = allItems.filter(
    (item) => item.checked
  ).length;
  const itemsSelectedQuantity = items.filter((item) => item.checked).length;

  const handleAllChecked = (checked) => {
    let changedItems = items.map((item) => ({
      ...item,
      checked,
    }));

    let otherItems = allItems.filter(
      (item) => !items.find((otherItem) => otherItem.localId === item.localId)
    );
    setAllItems([...changedItems, ...otherItems]);
    setItems(changedItems);
  };

  const handleChecked =
    ({ localId }) =>
    (checked) => {
      setAllItems(
        allItems.map((item) =>
          item.localId === localId ? { ...item, checked } : item
        )
      );
      setItems(
        items.map((item) =>
          item.localId === localId ? { ...item, checked } : item
        )
      );
    };

  const handleCerrar = () => setSideSheetOpen(false);
  const handleDismiss = () => {
    handleCerrar();
    onDismiss && onDismiss();
  };

  const handleApply = () => {
    onChange &&
      onChange(
        allItems
          .filter((item) => item.checked)
          .map(({ localId, ...item }) => item)
          .map(outFormat)
      );
    closeOnApply && handleCerrar();
  };

  const handleOnSearch = (value) => {
    if (value) {
      filterCallback &&
        setItems(allItems.filter((item) => filterCallback(item, value)));
    } else {
      setItems(allItems);
    }
  };

  const footer = (
    <S.Footer>
      <Button variant='secondary' size='md-full' onClick={handleCerrar}>
        Cancelar
      </Button>
      <Button
        id='aplicar_filtro'
        variant='primary'
        size='md-full'
        onClick={handleApply}
        disabled={allItemsSelectedQuantity === 0}
      >
        Aplicar
      </Button>
    </S.Footer>
  );

  const handlePreviousStepAction = () => {
    handleCerrar();
    previousStepAction && previousStepAction();
  };

  const handleClick = () => {
    setItems(allItems);
    setSideSheetOpen(true);
  };

  return (
    <>
      <SelectorButton
        selectedQuantity={allItemsSelectedQuantity}
        onClick={handleClick}
        id={id}
        bigCommerce={bigCommerce}
        cuit={cuit}
      >
        {title}
      </SelectorButton>
      <SideSheet
        isOpen={isSideSheetOpen}
        onDismiss={handleDismiss}
        title={title}
        footer={footer}
        size={'sm'}
        previousStepAction={previousStepAction && handlePreviousStepAction}
      >
        <S.ItemsSelector>
          {filterCallback && (
            <S.SearchWrapper>
              <SearchInput
                id={'search-input'}
                label={searchLabel}
                placeholder={searchPlaceholder}
                size={'md'}
                onSearch={handleOnSearch}
              />
            </S.SearchWrapper>
          )}
          {items.length === 0 ? (
            <S.ItemSelectorNoItems>
              <NoItems />
            </S.ItemSelectorNoItems>
          ) : (
            <>
              <S.SelectAll>
                <Checkbox
                  id='select-all'
                  checked={itemsSelectedQuantity > 0}
                  onChange={handleAllChecked}
                  isSelectAllCheck
                  size='sm'
                >
                  {allItemsSelectedQuantity === 0 && 'Seleccionar todos'}
                  {allItemsSelectedQuantity > 0 &&
                    `${allItemsSelectedQuantity} Seleccionados`}
                </Checkbox>
              </S.SelectAll>
              <S.Hr />
              <S.List>
                {items.map((data, key) => (
                  <li key={key}>
                    <S.Item>
                      <S.CheckboxWrapper>
                        <Checkbox
                          id={`items__selector--${key}`}
                          checked={data.checked}
                          onChange={handleChecked(data)}
                          size='sm'
                        />
                      </S.CheckboxWrapper>
                      {renderItem({ ...data, key })}
                    </S.Item>
                    <S.Hr />
                  </li>
                ))}
              </S.List>
            </>
          )}
        </S.ItemsSelector>
      </SideSheet>
    </>
  );
};
ItemsSelector.defaultProps = {
  initialData: [],
  renderItem: () => false,
  outFormat: (e) => e,
  closeOnApply: true,
  bigCommerce: false,
  cuit: '',
};

ItemsSelector.propTypes = {
  title: PropTypes.string,
  initialData: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  renderItem: PropTypes.func.isRequired,
  outFormat: PropTypes.func.isRequired,
  closeOnApply: PropTypes.bool,
  filterCallback: PropTypes.func,
  searchLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  onDismiss: PropTypes.func,
  previousStepAction: PropTypes.func,
  disabled: PropTypes.bool,
  bigCommerce: PropTypes.bool,
  cuit: PropTypes.string,
};

export default ItemsSelector;
